/**
 * admin-form-edit.js — 後台表單編輯頁「欄位驗證 + 提交流程」框架
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（無對應 form DOM 即 no-op）；另導出純預覽引擎
 *   initUploads(form) 供 cr-form-card.js（auth 端 .cr-form-card）重用，不含 submit/dirty/lock 提交意圖。
 *
 * 對象（form-validation-ux-spec §0 / §10）：
 *   clinic-edit  <form class="admin-clinic-form">
 *   doctor-edit  <form class="admin-doctor-form">
 *   article-edit <form class="admin-article-form">（雙主要動作：草稿 / 發布；含富文本 .editor）
 *   platform-products-edit <form class="admin-product-form">
 *
 * 以 member-edit.js 為標準範本（setInvalid/clearInvalid/submit spinner/AdminToast 沿用），
 * 補齊 spec P0+P1：欄位級即時驗證 + 提交流程 + 圖片上傳 UX + dirty 離開攔截 + 鎖定欄 hint。
 *
 * ── 與 clinic-hours.js 共存（任務關鍵，務必不破壞）──
 *   clinic-hours.js 在 capture 階段（addEventListener('submit', fn, true)）先跑 hours 驗證：
 *     - hours 有錯 → preventDefault + stopImmediatePropagation（擋掉所有後續 handler，含本檔 bubble）。
 *     - hours 通過 → 不 preventDefault、不 stop，傳播續行 → 本檔 bubble handler 接手驗一般欄位。
 *   本檔 submit handler 一律掛 **bubble**（預設第三參數 false），絕不掛 capture：
 *     ⇒ 永遠晚於 clinic-hours 的 capture handler，兩者不重複綁、不互蓋、不雙重 preventDefault 打架。
 *     ⇒ hours 錯時本檔不執行（不亮 spinner、不假成功）；hours 對時本檔才驗一般欄位。
 *   不引用、不 import、不改動 clinic-hours.js（已驗 29/29）。
 *
 * 行為層；無對應 form DOM 即 no-op（不影響其他 admin 頁）。本波無 backend，submit 模擬成功。
 *
 * ── 元件化階段 0 接點 ──
 *   - 欄位 invalid 三件套（setInvalid/clearInvalid/ensureErrorEl/swapDescribedBy）已收斂到
 *     field-error.js（單一真相源），本檔改 import，刪原內聯版本（行為等價）。
 *     注意新簽名：setInvalid(control, message) / clearInvalid(control)（error 節點由 helper 自解，
 *     不再傳 errorEl）。
 *   - validateField 在 HTML5 旗標判斷後、checkValidity() 兜底前，插入宣告式規則插槽
 *     runDeclaredRules（field-rules.js）。回傳契約不變（null | 字串）。
 *   - 階段 0 尚無頁面帶 data-rules，故規則插槽 / 群層 / richtext 額外迴圈全惰性，零行為變化。
 */

import { runDeclaredRules } from './field-rules.js';
import { setInvalid, clearInvalid } from './field-error.js';
import { prefersReducedMotion } from '../utils/dom.js';

/* spinner SVG：class 用通用 .admin-spinner（CSS 在 admin-forms2.css，重用 member-spin keyframe）。 */
const SPINNER_SVG =
  '<svg class="admin-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
  'stroke-dasharray="42" stroke-dashoffset="14" /></svg>';

const WARN_ICON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.5" aria-hidden="true">' +
  '<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 ' +
  '3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 ' +
  '0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>';

/* 每頁差異設定（spec §1 表）。以 form class 取對應 config。 */
const FORM_CONFIGS = [
  {
    selector: '.admin-clinic-form',
    submitLabel: '提交申請',
    submitBusyLabel: '送出中…',
    successType: 'success',
    successTitle: '送出成功',
    successText: '診所資料已送出，平台審核中，約 3 個工作天內以 Email 通知結果。',
    lockOnSuccess: true,             // 送審類：成功後全表單轉唯讀 + badge→審核中
    cancelTriggerSel: '[data-modal-trigger="#cancel-confirm-modal"]',
    leaveModalSel: '#cancel-confirm-modal',
    hasHours: true,
  },
  {
    selector: '.admin-doctor-form',
    submitLabel: '儲存並送審',
    submitBusyLabel: '送出中…',
    successType: 'success',
    successTitle: '送出成功',
    successText: '醫師資料已送出，執照審核中，約 3 個工作天內以 Email 通知結果。',
    lockOnSuccess: true,
    cancelTriggerSel: '[data-modal-trigger="#cancel-confirm-modal"]',
    leaveModalSel: '#cancel-confirm-modal',
  },
  {
    selector: '.admin-article-form',
    submitLabel: '發布送審',
    submitBusyLabel: '發布中…',
    successType: 'success',
    successTitle: '送出成功',
    successText: '文章已送出審核，通過後將自動發布。',
    lockOnSuccess: false,
    cancelTriggerSel: '[data-confirm-discard]',   // article 取消為 <a data-confirm-discard>
    leaveModalSel: '#discard-modal',
    hasEditor: true,
    draftBtnSel: '[data-action="save-draft"]',
    draftSuccessTitle: '草稿已儲存', // 需文案
    draftSuccessText: '您可稍後回來繼續編輯。', // 需文案
  },
  {
    selector: '.admin-product-form',
    submitLabel: '儲存',
    submitBusyLabel: '儲存中…',
    successType: 'success',
    successTitle: '儲存成功',
    successText: '產品資料已更新並即時生效。',
    lockOnSuccess: false,
    cancelTriggerSel: '[data-modal-trigger="#leave-confirm-modal"]',
    leaveModalSel: '#leave-confirm-modal',
  },
  {
    // platform-privacy 隱私政策編輯（Pilot ②，修黑洞②）。整頁原 0 個 <form>，發布鈕只開 modal、
    // 確認鈕無 handler → 可空白發布對外政策。現以 <form class="admin-privacy-form"> 包住
    // .privacy-layout，發布確認鈕（#publish-modal 內、表單外）以 form="privacy-edit-form" 關聯。
    //  - 非 modal 表單（主頁編輯流），故走一般 dirty / 離開攔截（取消為 <a data-confirm-discard>，
    //    對齊 article-edit 的 #discard-modal 慣例）。
    //  - lockOnSuccess=false：政策編輯為可重複編輯頁（隨時回來改 / 發新版），發布後不鎖整頁；
    //    且本頁 page-head 無 .badge，badge 翻轉本就 no-op。比照 article / product。
    //  - 發布／草稿「意圖同步」（按發布→設 publishStatus=發布 + 關 modal；按草稿→設草稿 + toast）
    //    由 privacy-edit.js 接（頁面語義層，引擎不管），確保條件式必填在發布時生效、草稿時放行。
    //  - 富文本（政策內文 hidden output）帶 data-rules="richtext-nonempty"，走下方 initSubmit 的
    //    [data-editor-output][data-rules] 額外迴圈驗（validateField 對 hidden 早退）。
    selector: '.admin-privacy-form',
    submitLabel: '確定發布',
    submitBusyLabel: '發布中…',
    successType: 'success',
    successTitle: '發布成功',
    successText: '隱私政策已發布並對全站生效，已建立版本快照可隨時還原。',
    lockOnSuccess: false,
    cancelTriggerSel: '[data-confirm-discard]',   // 取消為 <a data-confirm-discard>（同 article）
    leaveModalSel: '#discard-modal',
    hasEditor: true,                 // 政策內文富文本：initDirty 直掛 .editor__canvas input
  },
  {
    // doctors 新增醫師 modal（Pilot ①，修黑洞①）。不沿用 .admin-doctor-form：
    //  (1) doctor-edit 與本 modal 不同頁，但 init 以 querySelector 單選，混用同 class 易誤配；
    //  (2) modal 提交鈕在 footer 表單外（form= 關聯）、無 cancel-confirm/leave 流程、成功不該鎖整頁。
    // 故給 modal 專屬 config + isModal 旗標走關 modal 成功路徑、跳過 dirty/離開攔截。
    selector: '.admin-doctor-add-form',
    submitLabel: '送出審核',
    submitBusyLabel: '送出中…',
    successType: 'success',
    successTitle: '送出成功',
    successText: '醫師資料已送出，執照審核中，約 3 個工作天內以 Email 通知結果。',
    lockOnSuccess: false,
    isModal: true,
    successCloseModalSel: '#doctor-add-modal',
  },
];

/* 圖片上傳各欄規格（spec §6.2）。以 input id 對映 accept / 大小上限 / 是否必填 + 文案方向。
 * required 僅供 admin initSubmit 的 validateRequiredUploads 用；auth 的 .cr-form-card 走
 * cr-form-card.js 自有必填驗證（不呼叫 admin initSubmit），故此處 auth 欄的 required 旗標
 * 不被讀（保守標 false，避免誤導）。 */
const UPLOAD_RULES = {
  'clinic-cover': { maxMB: 5, required: false },
  'doctor-avatar': { maxMB: 2, required: false },
  'doctor-license-photo': { maxMB: 5, required: true }, // 必填（醫師執照照片）
  'doctor-cert': { maxMB: 5, required: false, allowPdf: true },
  'article-list-image': { maxMB: 5, required: false },  // 上限未定，暫採 5MB（需定規格）
  'article-hero-image': { maxMB: 5, required: false },  // 上限未定，暫採 5MB（需定規格）
  'product-image': { maxMB: 2, required: false },
  'product-leaflet': { maxMB: 10, required: false, allowPdf: true }, // 產品仿單 PDF（PDF 無縮圖，走 doctor-cert 無圖分支）
  'product-gallery': { maxMB: 5, required: false },                  // 產品圖庫（多圖 image/*；JS 取 files[0] 預覽，餘為 demo）
  // ── clinic-register（.cr-form-card，auth）各上傳欄：以各欄 accept / hint 文案對映 ──
  //   尺寸上限取頁面 hint 標示值；licenses / extra accept PDF → allowPdf:true（PDF 無縮圖，
  //   走 has-preview 無圖分支，與 doctor-cert 一致）。logo accept PNG/SVG，但預覽引擎
  //   fileExtAllowed 僅放行 raster（JPG/PNG/WebP）；SVG 預覽暫不支援（見回報觀察）。
  'clinic-logo': { maxMB: 2, required: false },                 // hint：2 MB 以內
  'doc-license-clinic': { maxMB: 10, required: false, allowPdf: true }, // hint：最大 10MB，PDF/JPG/PNG
  'doc-license-doctor': { maxMB: 10, required: false, allowPdf: true }, // hint：最大 10MB，PDF/JPG/PNG
  'doc-extra': { maxMB: 10, required: false, allowPdf: true },          // 選填補充文件，PDF/JPG/PNG
};

/* ====================================================================
 * 欄位 invalid 標記輔助 → 已收斂至 field-error.js（單一真相源），於檔頭 import。
 * 新簽名：setInvalid(control, message) / clearInvalid(control) / ensureErrorEl(control)。
 * （原內聯 setInvalid/clearInvalid/swapDescribedBy/ensureErrorEl 已刪，行為等價。）
 * ==================================================================== */

/* ====================================================================
 * 單欄驗證規則 — HTML5 為主 + 少量自訂（型別/長度/必填）。
 * 文案：spec §2.2 僅給方向，實際用字「需文案」（此處為工程暫定值）。
 * 回傳 null=通過，或錯誤訊息字串。
 * ==================================================================== */
function validateField(input) {
  // disabled / hidden 不驗（member-edit.js L135 同邏輯）
  if (input.disabled || input.type === 'hidden') return null;

  const label =
    (input.closest('.form-field')?.querySelector('.form-field__label')?.textContent || '')
      .replace(/\s*\*\s*$/, '')
      .trim();

  // 必填空值（HTML5 valueMissing）
  if (input.validity.valueMissing) {
    return label ? `請填寫${label}` : '此欄為必填'; // 需文案
  }
  // 型別錯（email / url / number / date）
  if (input.validity.typeMismatch) {
    if (input.type === 'email') return '請輸入有效的 Email，例如 name@example.com';
    if (input.type === 'url') return '請輸入完整網址，需含 https://（例：https://example.com）';
    return '此欄格式不符，請確認後重新輸入';
  }
  if (input.validity.patternMismatch) return '格式不符，請依欄位提示的格式輸入';
  if (input.validity.tooShort) {
    return `至少需 ${input.minLength} 字`; // 需文案
  }
  if (input.validity.tooLong) {
    return `不可超過 ${input.maxLength} 字`; // 需文案
  }
  if (input.validity.rangeUnderflow || input.validity.rangeOverflow || input.validity.stepMismatch) {
    // 數值範圍：從 input 的 min/max 屬性帶入具體區間；無 min/max 則回退通用句。
    const min = input.getAttribute('min');
    const max = input.getAttribute('max');
    if (min !== null && max !== null) return `數值需介於 ${min}–${max} 之間`;
    return '請輸入有效的數值';
  }
  // 宣告式規則插槽（field-rules.js）：HTML5 旗標都過後，跑欄位 data-rules 自訂規則。
  // 回傳契約不變（null | 字串）。階段 0 無頁面帶 data-rules → 一律 null（惰性）。
  const declared = runDeclaredRules(input, input.form);
  if (declared) return declared;
  // 相容性兜底：仍 invalid 但無對應旗標
  if (!input.checkValidity()) return '此欄填寫有誤，請確認後重新輸入';
  return null;
}

/* ====================================================================
 * 欄位級即時驗證綁定（spec §2.1：報錯延後 blur/submit、清錯提前 input）
 * ==================================================================== */
function initLiveValidation(form) {
  const fields = form.querySelectorAll('.form-field__input');
  fields.forEach((input) => {
    // blur：失敗才標紅（不在輸入中途報錯）
    input.addEventListener('blur', () => {
      if (input.disabled || input.type === 'hidden') return;
      const msg = validateField(input);
      if (msg) {
        setInvalid(input, msg);
      } else {
        clearInvalid(input);
      }
    });
    // input/change：已標紅的欄，達標即時清紅（select 用 change）
    const clearEvent = input.tagName === 'SELECT' ? 'change' : 'input';
    input.addEventListener(clearEvent, () => {
      if (input.getAttribute('aria-invalid') !== 'true') return;
      if (!validateField(input)) clearInvalid(input);
    });
  });
}

/* ====================================================================
 * 圖片上傳 UX（spec §6）：選檔→驗格式/大小→預覽；移除/替換。
 * ==================================================================== */
function fileExtAllowed(file, allowPdf) {
  const okImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  const okPdf = allowPdf && file.type === 'application/pdf';
  return okImage || okPdf;
}

function initUploads(form) {
  form.querySelectorAll('.upload-field__input[type="file"]').forEach((input) => {
    const field = input.closest('.upload-field');
    if (!field) return;
    const rule = UPLOAD_RULES[input.id] || { maxMB: 5, required: false };

    const setUploadError = (msg) => {
      field.setAttribute('aria-invalid', 'true');
      let errorEl = field.querySelector('.upload-field__error');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'upload-field__error';
        errorEl.setAttribute('role', 'alert');
        field.appendChild(errorEl);
      }
      errorEl.textContent = msg;
      errorEl.hidden = false;
    };
    const clearUploadError = () => {
      field.removeAttribute('aria-invalid');
      const errorEl = field.querySelector('.upload-field__error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    };
    const showPreview = (url) => {
      let preview = field.querySelector('.upload-field__preview');
      let img = preview ? preview.querySelector('img') : null;
      if (!preview) {
        // demo HTML 多已有 .has-preview + __preview；若無則建最小版（spec：補最小可用版）
        preview = document.createElement('div');
        preview.className = 'upload-field__preview';
        img = document.createElement('img');
        img.alt = '已上傳檔案預覽'; // 需文案
        img.decoding = 'async';
        preview.appendChild(img);
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'button button--icon upload-field__action';
        removeBtn.setAttribute('aria-label', '移除已上傳檔案'); // 需文案
        removeBtn.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.5" stroke="currentColor" ' +
          'aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" ' +
          'd="M6 18 18 6M6 6l12 12" /></svg>';
        preview.appendChild(removeBtn);
        field.appendChild(preview);
      }
      if (img) img.src = url;
      field.classList.add('has-preview');
    };

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (!file) return;
      // 格式
      if (!fileExtAllowed(file, rule.allowPdf)) {
        input.value = '';
        setUploadError(
          rule.allowPdf
            ? '僅接受 JPG / PNG / PDF 檔，請更換檔案格式'
            : '僅接受 JPG / PNG 檔，請更換檔案格式'
        );
        return;
      }
      // 大小
      if (file.size > rule.maxMB * 1024 * 1024) {
        input.value = '';
        setUploadError(`檔案超過 ${rule.maxMB} MB，請壓縮後再上傳`); // 需文案
        return;
      }
      clearUploadError();
      // PDF 無縮圖 → 不注圖片預覽（標需 PDF 預覽 fallback，spec §6.2 P2）；圖片才預覽
      if (file.type !== 'application/pdf') {
        const url = URL.createObjectURL(file);
        const existingImg = field.querySelector('.upload-field__preview img');
        if (existingImg && existingImg.dataset.objUrl) {
          URL.revokeObjectURL(existingImg.dataset.objUrl);
        }
        showPreview(url);
        const img = field.querySelector('.upload-field__preview img');
        if (img) img.dataset.objUrl = url;
      } else {
        field.classList.add('has-preview');
      }
    });

    // 移除 / 替換（事件代理於 field：__action click）
    field.addEventListener('click', (e) => {
      const action = e.target.closest('.upload-field__action');
      if (!action || !field.contains(action)) return;
      e.preventDefault();
      const img = field.querySelector('.upload-field__preview img');
      if (img && img.dataset.objUrl) {
        URL.revokeObjectURL(img.dataset.objUrl);
        delete img.dataset.objUrl;
      }
      input.value = '';
      field.classList.remove('has-preview');
      clearUploadError();
    });
  });
}

/* submit 時的必填上傳檢查：回傳第一個未上傳的必填 upload input（供捲動 focus），或 null。 */
function validateRequiredUploads(form) {
  let firstMissing = null;
  form.querySelectorAll('.upload-field__input[type="file"]').forEach((input) => {
    const rule = UPLOAD_RULES[input.id];
    if (!rule || !rule.required) return;
    const field = input.closest('.upload-field');
    const hasFile = (input.files && input.files.length > 0) ||
      (field && field.classList.contains('has-preview')); // demo 已 has-preview 視為已上傳
    if (!hasFile) {
      field.setAttribute('aria-invalid', 'true');
      let errorEl = field.querySelector('.upload-field__error');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.className = 'upload-field__error';
        errorEl.setAttribute('role', 'alert');
        field.appendChild(errorEl);
      }
      errorEl.textContent = '請上傳此必填檔案'; // 需文案（doctor 執照照片）
      errorEl.hidden = false;
      if (!firstMissing) firstMissing = input;
    }
  });
  return firstMissing;
}

/* ====================================================================
 * 提交失敗 summary alert（spec §3.4 A / P1-5）：≥2 錯時頂部摘要 + 聚焦。
 * 重用 .admin-alert--danger 視覺；本檔加 .admin-form-summary 定位 class。
 * ==================================================================== */
function renderSummary(form, errors) {
  removeSummary(form);
  if (errors.length < 2) return null;
  const box = document.createElement('section');
  box.className = 'admin-alert admin-alert--danger admin-form-summary';
  box.setAttribute('role', 'alert');
  box.setAttribute('tabindex', '-1');
  box.setAttribute('aria-label', '表單錯誤摘要'); // 需文案

  const title = document.createElement('p');
  title.className = 'admin-alert__title';
  const icon = document.createElement('span');
  icon.className = 'admin-alert__icon';
  icon.innerHTML = WARN_ICON_SVG;
  title.appendChild(icon);
  title.appendChild(document.createTextNode(`有 ${errors.length} 個欄位需要修正`)); // 需文案
  box.appendChild(title);

  const list = document.createElement('ul');
  list.className = 'admin-alert__list';
  errors.forEach(({ input, message }) => {
    const li = document.createElement('li');
    li.className = 'admin-alert__row';
    if (input && (input.id || input.name)) {
      const link = document.createElement('a');
      link.href = `#${input.id || ''}`;
      link.textContent = message;
      link.addEventListener('click', (e) => {
        e.preventDefault();
        scrollToField(input);
      });
      li.appendChild(link);
    } else {
      li.textContent = message;
    }
    list.appendChild(li);
  });
  box.appendChild(list);

  form.insertBefore(box, form.firstChild);
  return box;
}

function removeSummary(form) {
  const old = form.querySelector('.admin-form-summary');
  if (old) old.remove();
}

/* 捲到欄位 + focus（spec §3.4 / P1-3）。reduced-motion 用 auto（瞬移）。 */
function scrollToField(input) {
  if (!input) return;
  input.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'center',
  });
  // preventScroll 避免 focus 與 smooth scroll 互搶
  input.focus({ preventScroll: true });
}

/* ====================================================================
 * 提交流程（spec §3）：spinner + 模擬成功 + 送審類唯讀/badge 翻轉。
 * ==================================================================== */
// TODO(backend): runSubmitSuccess 的 setTimeout 換 fetch 各資源 CRUD（含圖片/富文本），成功才 lock/toast、失敗還原 — 見 docs/BACKEND_HANDOFF.md#admin-form-edit
function runSubmitSuccess(form, cfg, submitBtn) {
  const originalLabel = submitBtn.textContent;
  form.classList.add('is-submitting');
  form.setAttribute('aria-busy', 'true');
  submitBtn.setAttribute('aria-disabled', 'true');
  submitBtn.disabled = true;
  submitBtn.innerHTML = prefersReducedMotion()
    ? cfg.submitBusyLabel
    : SPINNER_SVG + `<span>${cfg.submitBusyLabel}</span>`;

  // submitting 期間放行 beforeunload（避免成功跳轉被攔）
  form.dataset.submitting = 'true';

  window.setTimeout(() => {
    form.classList.remove('is-submitting');
    form.removeAttribute('aria-busy');
    delete form.dataset.submitting;
    submitBtn.removeAttribute('aria-disabled');
    submitBtn.disabled = false;
    submitBtn.textContent = originalLabel;

    // 清 dirty（成功後不再攔離開）
    clearDirty(form);

    if (window.AdminToast && typeof window.AdminToast.show === 'function') {
      window.AdminToast.show({
        type: cfg.successType,
        title: cfg.successTitle,
        text: cfg.successText,
      });
    }

    // modal 表單（doctors 新增）：成功後關 modal（不鎖整頁、不翻 page-head badge——modal 無此語義）。
    // demo 無 backend，不真正新增卡片；關 modal + toast 即完成提交回饋。
    if (cfg.isModal) {
      if (cfg.successCloseModalSel && window.Modal && typeof window.Modal.close === 'function') {
        window.Modal.close(cfg.successCloseModalSel);
      }
      return;
    }

    // 送審類：全表單轉唯讀 + page-head badge → 送審中（spec §3.3）
    if (cfg.lockOnSuccess) lockFormAsSubmitted(form);
  }, 900);
}

/* 送審成功 → 全欄 disabled + 主鈕 disabled + badge 翻轉「送審中」。 */
function lockFormAsSubmitted(form) {
  form.querySelectorAll('input, select, textarea, button').forEach((el) => {
    el.disabled = true;
  });
  const badge = document.querySelector('.admin-page-head .badge');
  if (badge) {
    badge.className = 'badge badge--info';
    badge.textContent = '審核中';
  }
}

function initSubmit(form, cfg) {
  // 主鈕：以 [type=submit] 取；article 有兩顆按鈕（草稿 button + 發布 submit）。
  // modal 表單（doctors 新增）的提交鈕在 modal__footer、表單外，以 form="<id>" 屬性關聯
  //  → 不在 .admin-form-actions 內，故 in-form 找不到時 fallback 找關聯鈕。
  const submitBtn =
    form.querySelector('.admin-form-actions .button[type="submit"]') ||
    (form.id ? document.querySelector(`button[type="submit"][form="${form.id}"]`) : null);
  if (!submitBtn) return;

  // ── bubble 階段（絕不 capture）：clinic-hours capture handler 先跑，hours 錯會
  //    stopImmediatePropagation 擋掉本 handler；hours 對才輪到本檔（共存關鍵）。──
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (form.classList.contains('is-submitting')) return; // 鎖重入（spec §1 ①）

    // 全欄齊驗 + 收集錯誤（含未碰過的欄）
    const errors = [];
    let firstInvalid = null;
    form.querySelectorAll('.form-field__input').forEach((input) => {
      if (input.disabled || input.type === 'hidden') return;
      const msg = validateField(input);
      if (msg) {
        setInvalid(input, msg);
        errors.push({ input, message: msg });
        if (!firstInvalid) firstInvalid = input;
      } else {
        clearInvalid(input);
      }
    });

    // ── 群層宣告式規則（checkbox-min 等）：validateField 不掃群容器，故獨立一輪。──
    //    群容器（.check-grid[data-field-group]）本身非 .form-field，錯誤節點為其直接子
    //    .form-field__error（factory 預置）。階段 0 無頁面帶 [data-field-group][data-rules] → 惰性。
    form.querySelectorAll('[data-field-group][data-rules]').forEach((groupEl) => {
      if (groupEl.matches(':disabled')) return;
      const msg = runDeclaredRules(groupEl, form);
      const groupError = groupEl.querySelector(':scope > .form-field__error');
      if (msg) {
        groupEl.setAttribute('aria-invalid', 'true');
        if (groupError) {
          groupError.textContent = msg;
          groupError.hidden = false;
        }
        errors.push({ input: groupEl, message: msg });
        if (!firstInvalid) firstInvalid = groupEl;
      } else {
        groupEl.removeAttribute('aria-invalid');
        if (groupError) groupError.hidden = true;
      }
    });

    // ── 富文本（richtext-nonempty 等）：承接編輯器輸出的 hidden input（validateField 對
    //    type=hidden 早退），故必須走此額外迴圈。setInvalid 走 .form-field 包裹 → error 顯於
    //    可視欄位下方。階段 0 無頁面帶 input[data-editor-output][data-rules] → 惰性。──
    form.querySelectorAll('input[data-editor-output][data-rules]').forEach((hidden) => {
      const msg = runDeclaredRules(hidden, form);
      if (msg) {
        setInvalid(hidden, msg);
        errors.push({ input: hidden, message: msg });
        if (!firstInvalid) firstInvalid = hidden;
      } else {
        clearInvalid(hidden);
      }
    });

    // 必填上傳（doctor 執照照片）
    const missingUpload = validateRequiredUploads(form);
    if (missingUpload) {
      errors.push({ input: missingUpload, message: '請上傳此必填檔案' }); // 需文案
      if (!firstInvalid) firstInvalid = missingUpload;
    }

    if (errors.length) {
      renderSummary(form, errors);
      const summary = form.querySelector('.admin-form-summary');
      if (summary) {
        // ≥2 錯：聚焦 summary（a11y 最佳實務），其錨點可跳各欄
        summary.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        summary.focus({ preventScroll: true });
      } else if (firstInvalid) {
        scrollToField(firstInvalid);
      }
      return; // 保留所有輸入值（天條），不亮 spinner
    }

    removeSummary(form);
    runSubmitSuccess(form, cfg, submitBtn);
  });

  // article 草稿鈕：跳過完整驗證、只存 + toast「草稿已儲存」（spec §1 / U7）
  if (cfg.draftBtnSel) {
    const draftBtn = form.querySelector(cfg.draftBtnSel);
    if (draftBtn) {
      draftBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (form.classList.contains('is-submitting')) return;
        clearDirty(form);
        if (window.AdminToast && typeof window.AdminToast.show === 'function') {
          window.AdminToast.show({
            type: 'success',
            title: cfg.draftSuccessTitle,
            text: cfg.draftSuccessText,
          });
        }
      });
    }
  }
}

/* ====================================================================
 * dirty 追蹤 + 離開攔截（spec §5）
 * ==================================================================== */
function markDirty(form) {
  form.dataset.dirty = 'true';
}
function clearDirty(form) {
  delete form.dataset.dirty;
}
function isDirty(form) {
  return form.dataset.dirty === 'true';
}

function initDirty(form, cfg) {
  // 一般欄位變動（input/change 冒泡含 select/checkbox/radio/file/textarea）
  form.addEventListener('input', () => markDirty(form));
  form.addEventListener('change', () => markDirty(form));

  // 富文本（article）：監聽 .editor__canvas 的 input（contenteditable input 冒泡到 form，
  // 但 editor 在 form 內，故 form 的 input listener 已能接到；此處保險直掛 canvas）。
  if (cfg.hasEditor) {
    const canvas = form.querySelector('.editor__canvas[contenteditable]');
    if (canvas) canvas.addEventListener('input', () => markDirty(form));
  }

  // beforeunload（關分頁 / 重整）：dirty 且非提交中才攔
  window.addEventListener('beforeunload', (e) => {
    if (isDirty(form) && form.dataset.submitting !== 'true') {
      e.preventDefault();
      e.returnValue = ''; // 現代瀏覽器顯示原生對話框（文案不可自訂）
    }
  });

  // 站內離開攔截：取消鈕 / 麵包屑連結。dirty → 開 Modal；非 dirty → 直接導航。
  initLeaveIntercept(form, cfg);
}

function initLeaveIntercept(form, cfg) {
  // 取消鈕：
  //  - clinic/doctor/product 取消已帶 data-modal-trigger（modal.js 綁在元素上）。
  //    用 capture document 監聽搶在 modal.js 之前：非 dirty → 直接導航到 modal 內離開連結 +
  //    stopImmediatePropagation 阻止 modal 開；dirty → 不擋（讓 modal.js 照常開）。
  //  - article 取消是 <a data-confirm-discard href>（無 modal-trigger）：
  //    dirty → preventDefault 開 discard modal；非 dirty → 放行（直接走 href）。
  const cancelEl = form.parentElement
    ? findCancelEl(cfg)
    : null;

  // 找離開連結（modal footer 內的 <a href>）作為「非 dirty 直接導航」目的地
  const leaveHref = getLeaveHref(cfg);

  document.addEventListener(
    'click',
    (e) => {
      const trigger = e.target.closest(cfg.cancelTriggerSel);
      if (!trigger) return;

      if (cfg.cancelTriggerSel === '[data-confirm-discard]') {
        // article：<a> 取消
        if (isDirty(form)) {
          e.preventDefault();
          openLeaveModal(cfg.leaveModalSel);
        }
        // 非 dirty：放行 <a> 原生導航（不擋）
        return;
      }

      // clinic/doctor/product：button[data-modal-trigger]
      if (!isDirty(form)) {
        // 非 dirty → 不開 Modal，直接導航
        e.preventDefault();
        e.stopImmediatePropagation(); // 擋掉 modal.js 在同元素上的 click handler
        if (leaveHref) window.location.href = leaveHref;
      }
      // dirty → 不動，讓 modal.js 照常開 Modal
    },
    true // capture：搶在 modal.js（綁在 trigger 元素）之前
  );

  // 麵包屑 / 返回鈕（站內導航連結）：dirty 時攔，開 Modal
  initBreadcrumbIntercept(form, cfg, leaveHref);
  void cancelEl;
}

function findCancelEl(cfg) {
  return document.querySelector(cfg.cancelTriggerSel);
}

/* 從離開 Modal footer 取「離開不儲存 / 捨棄變更」連結的 href（非 dirty 直接導航用）。 */
function getLeaveHref(cfg) {
  const modal = document.querySelector(cfg.leaveModalSel);
  if (!modal) return null;
  const link = modal.querySelector('.modal__footer a[href]');
  return link ? link.getAttribute('href') : null;
}

function openLeaveModal(sel) {
  if (window.Modal && typeof window.Modal.open === 'function') {
    window.Modal.open(sel);
  }
}

/* 麵包屑 / 返回列表連結攔截：dirty 時開 Modal（spec §5.2）。 */
function initBreadcrumbIntercept(form, cfg, leaveHref) {
  const navLinks = document.querySelectorAll(
    '.admin-breadcrumb a[href], .admin-detail-back a[href], .admin-page-back a[href], ' +
    '.admin-page-head__actions a[href]'
  );
  navLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      if (!isDirty(form)) return; // 非 dirty 放行
      e.preventDefault();
      // 暫存目標，Modal 內「離開」鈕走各自 href；此處統一開離開 Modal。
      openLeaveModal(cfg.leaveModalSel);
    });
  });
  void leaveHref;
}

/* ====================================================================
 * 鎖定 / disabled 欄位 hint（spec §4.2）：為真 disabled 的一般欄補說明 hint。
 * platform-locked（可選 select）已由 HTML 鎖 icon + hint 處理，不在此。
 * ==================================================================== */
function initLockedHints(form) {
  form.querySelectorAll('.form-field__input:disabled').forEach((input) => {
    const wrap = input.closest('.form-field');
    if (!wrap) return;
    if (wrap.querySelector('.form-field__hint')) return; // 已有 hint 不重複
    const hint = document.createElement('p');
    hint.className = 'form-field__hint';
    hint.textContent = '此欄由平台管理，如需變更請聯繫平台客服。'; // 需文案
    wrap.appendChild(hint);
  });
}

/* ====================================================================
 * init
 * ==================================================================== */
function initForm(form, cfg) {
  initLiveValidation(form);
  initUploads(form);
  initLockedHints(form);
  initSubmit(form, cfg);
  // modal 表單（doctors 新增）不接 dirty / 離開攔截：modal 以 data-modal-dismiss 關閉，
  // 無 cancel-confirm/breadcrumb 流程；且 beforeunload / 麵包屑攔截屬整頁編輯語義，
  // 掛在短命 modal 上會在關閉後仍誤攔導航。提交成功路徑由 cfg.isModal 走關 modal 分支。
  if (!cfg.isModal) initDirty(form, cfg);
}

function init(root = document) {
  FORM_CONFIGS.forEach((cfg) => {
    const form = root.querySelector(cfg.selector);
    if (form) initForm(form, cfg);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

/* initUploads 對外導出：供 cr-form-card.js 為 .cr-form-card（auth 多 step wizard）接線
 * 圖片預覽引擎。僅此單一純函式被重用——它只綁 file input 的 change/click 做預覽/格式·大小
 * 驗證 + 移除/替換，完全不涉 submit / save / dirty / lock 等 admin 提交意圖（那些由 initSubmit
 * / initDirty 持有，不導出）。admin 六表單仍走 initForm → initUploads 原路徑，不受影響。 */
export { init, initUploads };
